import React, {useState, useEffect} from "react";
import {VsiBubble, VsiH4, VsiH5} from "../../../styled/App.styled";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {Row, Col, Card, Button, Modal, ModalHeader, ModalFooter} from "reactstrap";
import Grid from "../../../components/Grid";
import {switchesSummary} from "../../../utils/gridColumnDefs/switches/switchesSummary";
import Stockchart from "../../../components/Stockchart";
import PieChart from "../../../components/PieChart";
import {formatStockChartConfig, snakeCaseToTitleCase} from "../../../utils/Helpers";
import {standardCapacityColors} from "../../../utils/chartUtils";
import {useNavigate} from "react-router-dom";
import {GetAuth, PostAuth} from "../../../utils/Verbs";

const errorFields = ['buffer_credit', 'link_failure', 'crc_errors', 'link_reset', 'loss_of_sync_signal', 'free_ports'];

export default function SwitchesSummary({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const {theme, client, periodId, user, getAccessTokenSilently} = useVsiAppContext();

    let navigate = useNavigate();
    const [pieData, setPieData] = useState({});
    const [errorTrendData, setErrorTrendData] = useState([]);
    const [portTrendData, setPortTrendData] = useState([]);
    const [rowSelected, setRowSelected] = useState(null);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [data, setData] = useState([]);
    // const [data, {isLoading}] = useDataWithRoute(`/api/v6/report/enterprise_switch_proc?client_id=${client.clientId}&period_id=${periodId}`);


    const getSwitchSummary = (data) => {
        setPieData({
            name: 'Ports',
            colorByPoint: true,
            data: mungePie(data?.[0] || [])
        });

        const allDates = {};
        const findSwitches = {};
        (data?.[1] || [])?.forEach(datum => {
            if (!allDates.hasOwnProperty(datum.collection_date)) {
                allDates[datum.collection_date] = {...datum};
            } else {
                errorFields.forEach(field => {
                    allDates[datum.collection_date][field] = parseInt(allDates?.[datum.collection_date]?.[field] || 0) + parseInt(datum?.[field] || 0);
                });
            }

            if (!findSwitches[datum.global_name]) {
                findSwitches[datum.global_name] = true;
            }
        });

        const fieldsSwitches = Object.keys(findSwitches).map(sanSwitch => ({
            name: sanSwitch,
            field: 'free_ports',
        }));

        const allErrorKeys = Object.keys(allDates).sort((a, b) => new Date(a) - new Date(b));
        const allErrorsTrend = allErrorKeys.map(key => allDates[key]);

        const allErrorTrends = formatStockChartConfig((allErrorsTrend || []),
            [{name: 'Buffer Credit', field: 'buffer_credit',},
                {name: 'Link Failure', field: 'link_failure',},
                {name: 'Link Reset', field: 'link_reset',},
                {name: 'CRC Errors', field: 'crc_errors',},
                {name: 'Loss of Sync Signal', field: 'loss_of_sync_signal',},
            ]);

        const allPortsTrends = formatStockChartConfig(data?.[1] || [], fieldsSwitches, 'global_name');

        setErrorTrendData(allErrorTrends.series);
        setPortTrendData(allPortsTrends.series);

    }

    const mungePie = data => {
        const keys = ['free_ports', 'used_ports', 'unlicensed_ports'];
        const dict = {};

        data.forEach(datum => {
            keys.forEach(key => {
                if (!dict.hasOwnProperty(key)) {
                    dict[key] = 0;
                }
                dict[key] += datum?.[key] || 0;
            })
        });

        return keys.map(key => ({
            name: snakeCaseToTitleCase(key),
            y: dict[key]
        }));
    };

    const filterChanged = (eventData) => {
        let filteredData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if (node.data) filteredData.push(node.data);
        });

        getSwitchSummary([filteredData, data?.recordsets?.[1]]);
    }
    const reloadData = () => {
        getAccessTokenSilently().then(token => {
            GetAuth(`/api/v6/report/enterprise_switch_proc?client_id=${client.clientId}&period_id=${periodId}`, token).then(data => {
                setData(data?.data);
            });
        });
    }
    useEffect(() => {
        getAccessTokenSilently().then(token => {
            GetAuth(`/api/v6/report/enterprise_switch_proc?client_id=${client.clientId}&period_id=${periodId}`, token).then(data => {
                setData(data?.data);
            });
        });

    }, [client, periodId]);

    useEffect(() => {
        getSwitchSummary(data?.recordsets || []);
    }, [data]);

    const onRowDoubleClick = (e) => {
        const {switch_name, device_name} = e.data;
        navigate(`/reports/san-switch/switch/switch-summary?switch_name=${encodeURIComponent(switch_name || device_name)}&period_id=${periodId}`)
    };

    const deleteDeviceBtn = <>
        <Button
            onClick={() => {
                setShowWarningModal(true);
            }}
            id={"upload-trash-btn"}
            disabled={!rowSelected}>
            <i className="fa fa-trash" aria-hidden="true"></i>
        </Button>
        <Modal className="warning-modal" backdrop="static" isOpen={showWarningModal}>
            <ModalHeader toggle={() => setShowWarningModal(false)}>Are you sure you want to delete
                Switch {rowSelected?.device_name}</ModalHeader>
            <ModalFooter>
                <Button onClick={async () => {
                    const formData = new FormData();
                    formData.append("client_id", rowSelected.client_id);
                    formData.append("period_id", rowSelected.period_id);
                    formData.append("device_name", rowSelected.device_name);
                    formData.append("device_type_code", rowSelected.device_type_code);
                    const token = await getAccessTokenSilently();
                    PostAuth('/api/v6/upload/delete_device', formData, token)
                        .then(data => {
                            PostAuth('/api/v6/report/delete_switch_summary',
                                {id: rowSelected.enterprise_switch_id}, token).then(() => {
                                reloadData();
                                setShowWarningModal(false);
                            });
                        });
                }
                } color="danger">Confirm Delete</Button>
                <Button color="secondary" onClick={() => {
                    setShowWarningModal(false)
                }}>Cancel</Button>
            </ModalFooter>
        </Modal>
    </>

    const onSelectionChanged = (gridRef) => {
        const selectedRow = gridRef?.current?.api?.getSelectedRows?.()?.[0];
        if (selectedRow) {
            setRowSelected(selectedRow);
        }
    };

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Enterprise SAN Switch Summary</VsiH4>
            <Row>
                <Col xs={12} lg={4}>
                    <VsiBubble autobotName='ErrorCountStockchart'>
                        <Stockchart config={{
                            colors: standardCapacityColors,
                            yAxis: {
                                title: {
                                    text: 'Error Count'
                                },
                            }
                        }} series={errorTrendData}/>
                    </VsiBubble>
                </Col>
                <Col xs={12} lg={4}>
                    <VsiBubble>
                        <PieChart
                            title={"Ports"}
                            height={600}
                            series={pieData}
                            includeLegend={true}/>
                    </VsiBubble>
                </Col>
                <Col xs={12} lg={4}>
                    <VsiBubble autobotName='FreePortsStockchart'>
                        <Stockchart config={{
                            yAxis: {
                                title: {
                                    text: 'Free Ports'
                                },
                            }
                        }} series={portTrendData}/>
                    </VsiBubble>
                </Col>
            </Row>
            <VsiH5 className={"mt-2"}>All SAN Switches</VsiH5>
            <VsiBubble>
                <Grid addStyle data={data?.recordsets?.[0] || []}
                      rowDoubleClicked={onRowDoubleClick}
                      gridText={'Double click to see detailed SAN-switch reports'}
                      filterChanged={filterChanged}
                      columnDefs={switchesSummary}
                      rowSelection={'single'}
                      onSelectionChanged={onSelectionChanged}
                      customToolbarButton={user.role === "super_user" ? deleteDeviceBtn : <></>}
                />
            </VsiBubble>
        </Card>
    )
}
